import type { ShopProductPreview } from '@kanbu/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { ChevronLeft, ChevronRight, HelpCircle, Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useContext, useEffect, useState } from 'react';

import { ChatRendererContext } from '../../../components/chat-renderer/chat-renderer-provider';
import { cn } from '../../../lib/cn';
import { formatPrice } from '../../../lib/format-price';
import { Button, Card, Carousel, type CarouselApi } from '../../../ui';

export type ShopProductCarouselItem = ShopProductPreview & {
  /** Agent-authored "why this product fits" note, shown on demand. */
  reason?: string | null;
};

export interface ShopProductCarousel {
  items: ShopProductCarouselItem[];
}

/**
 * One product card. When the agent provided a reason, a small "?" toggle
 * swaps the catalog description for the tailored "why this product" note —
 * kept inside the card (the card clips overflow, so no floating popover).
 */
function ShopProductCard({
  item,
  galleryEnabled,
}: {
  item: ShopProductCarouselItem;
  galleryEnabled: boolean;
}) {
  const chat = useContext(ChatRendererContext);
  const { t } = useLingui();
  const [showReason, setShowReason] = useState(false);

  const [imageIndex, setImageIndex] = useState(0);
  const images = [
    ...new Set(
      [item.imageUrl, ...(item.images ?? [])].flatMap(url =>
        url ? [url] : [],
      ),
    ),
  ];
  const activeImage = imageIndex % (images.length || 1);

  return (
    <Card.Root className='flex h-full w-full min-w-0 flex-col overflow-hidden border-chat-border shadow-none'>
      {images.length > 0 ? (
        <div className='group/gallery relative aspect-[4/3] w-full shrink-0 overflow-hidden bg-chat-background-secondary'>
          <img
            src={images[activeImage]}
            alt={item.title || item.name}
            loading='lazy'
            draggable={false}
            className='absolute inset-0 size-full object-cover'
          />
          {galleryEnabled && images.length > 1 ? (
            <div className='absolute inset-0 opacity-0 transition-opacity group-hover/gallery:opacity-100 [&:has(:focus-visible)]:opacity-100 [@media(hover:none)]:opacity-100'>
              <button
                type='button'
                aria-label={t`Previous image`}
                onClick={() =>
                  setImageIndex(
                    (activeImage + images.length - 1) % images.length,
                  )
                }
                className='absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-chat-background/90 p-1 text-chat-foreground'
              >
                <ChevronLeft className='size-4' />
              </button>
              <button
                type='button'
                aria-label={t`Next image`}
                onClick={() => setImageIndex((activeImage + 1) % images.length)}
                className='absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-chat-background/90 p-1 text-chat-foreground'
              >
                <ChevronRight className='size-4' />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      <Card.Content className='flex min-w-0 flex-1 flex-col p-2.5 select-none'>
        <div className='mb-1 flex items-start justify-between gap-1'>
          <h3 className='min-w-0 text-sm font-semibold leading-snug line-clamp-2'>
            {item.title || item.name}
          </h3>
          {item.reason ? (
            <button
              type='button'
              title={item.reason}
              aria-label={t`Why this product?`}
              aria-pressed={showReason}
              onClick={() => setShowReason(current => !current)}
              className={cn(
                'mt-0.5 shrink-0 text-muted-foreground/70 transition-colors hover:text-chat-foreground',
                showReason && 'text-chat-foreground',
              )}
            >
              <HelpCircle className='size-3.5' />
            </button>
          ) : null}
        </div>
        {showReason && item.reason ? (
          <p className='mb-2 line-clamp-4 text-xs italic text-muted-foreground'>
            <span className='font-medium not-italic'>
              <Trans>Why this product?</Trans>
            </span>{' '}
            {item.reason}
          </p>
        ) : (
          <p className='mb-2 line-clamp-3 text-xs text-muted-foreground'>
            {item.description}
          </p>
        )}
        <div className='mt-auto flex items-center justify-between gap-2'>
          <span className='shrink-0 whitespace-nowrap text-sm font-bold text-chat-foreground'>
            {formatPrice(item.price, item.currency) ?? t`Price not available`}
          </span>
          <div className='flex shrink-0 items-center gap-1'>
            <Button
              type='button'
              size='xs'
              variant='ghost'
              className='h-6 px-2 text-muted-foreground hover:bg-chat-accent hover:text-chat-foreground'
              disabled={!chat?.append || chat.isFetching}
              onClick={() =>
                chat?.append?.(
                  t`Find products similar to ${item.name} (${item.url}).`,
                  { uiAction: 'find_similar_products' },
                )
              }
            >
              <Trans>Similar</Trans>
            </Button>
            <Button
              asChild
              size='xs'
              variant='secondary'
              className='h-6 shrink-0'
            >
              <a href={item.url} target='_blank' rel='noopener noreferrer'>
                <Trans>Details</Trans>
              </a>
            </Button>
          </div>
        </div>
      </Card.Content>
    </Card.Root>
  );
}

type HorizontalBounds = Pick<DOMRect, 'left' | 'right'>;

export function getGalleryEnabledIndices({
  viewport,
  cards,
}: {
  viewport: HorizontalBounds;
  cards: HorizontalBounds[];
}): number[] {
  return cards.flatMap((card, index) =>
    card.left >= viewport.left - 1 && card.right <= viewport.right + 1
      ? [index]
      : [],
  );
}

/**
 * Carousel component, can be used to display a list of products
 * in the chat UI.
 */
export function ShopProductCarousel({ items }: ShopProductCarousel) {
  const { t } = useLingui();
  const chat = useContext(ChatRendererContext);
  const [api, setApi] = useState<CarouselApi>();
  const [visibleIndices, setVisibleIndices] = useState<number[]>([0]);

  useEffect(() => {
    if (!api) return;

    const updateSelection = () => {
      const viewport = api.rootNode().getBoundingClientRect();
      if (viewport.width === 0) return;

      const cards = api
        .slideNodes()
        .map(slide =>
          (slide.firstElementChild ?? slide).getBoundingClientRect(),
        );
      const next = getGalleryEnabledIndices({ viewport, cards });
      setVisibleIndices(current =>
        current.length === next.length &&
        current.every((index, i) => index === next[i])
          ? current
          : next,
      );
    };
    updateSelection();
    api.on('scroll', updateSelection);
    api.on('settle', updateSelection);
    api.on('reInit', updateSelection);

    return () => {
      api.off('scroll', updateSelection);
      api.off('settle', updateSelection);
      api.off('reInit', updateSelection);
    };
  }, [api]);

  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <Carousel.Root
      setApi={setApi}
      opts={{ align: 'center' }}
      className='mt-2 w-full min-w-0 [&>div:first-child]:overflow-visible'
    >
      <Carousel.Content>
        <AnimatePresence>
          {items.map((item, index) => (
            <Carousel.Item
              className='min-w-0 basis-[min(80%,20rem)]'
              key={item.id}
            >
              <motion.div
                className='h-full min-w-0'
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.3,
                  delay: 0.1 + index * 0.05,
                  ease: 'easeOut',
                }}
              >
                <ShopProductCard
                  item={item}
                  galleryEnabled={visibleIndices.includes(index)}
                />
              </motion.div>
            </Carousel.Item>
          ))}
        </AnimatePresence>
        <Carousel.Item className='min-w-0 basis-[min(80%,20rem)]'>
          <Card.Root className='flex h-full min-h-48 items-center justify-center border-chat-border p-4 shadow-none'>
            <Button
              type='button'
              variant='secondary'
              disabled={!chat?.append || chat.isFetching}
              onClick={() =>
                chat?.append?.(t`Show me more similar products.`, {
                  uiAction: 'show_more_products',
                })
              }
            >
              <Plus className='mr-1 size-4' />
              <Trans>Show more</Trans>
            </Button>
          </Card.Root>
        </Carousel.Item>
      </Carousel.Content>

      <Carousel.Previous
        type='button'
        aria-label={t`Previous products`}
        className='left-2 z-10 size-10 border-chat-border bg-chat-background/95 p-0 shadow-md disabled:hidden [&_svg]:size-5'
      />
      <Carousel.Next
        type='button'
        aria-label={t`Next products`}
        className='right-2 z-10 size-10 border-chat-border bg-chat-background/95 p-0 shadow-md disabled:hidden [&_svg]:size-5'
      />
    </Carousel.Root>
  );
}
